
/**
 * Module dependencies.
 */

import { BaseSelect, type ItemOption, type MenuAction } from '../../select';
import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { ReactNode, useCallback } from 'react';
import { isNil } from 'lodash';
import { useField, useFormState } from '@seegno/react-forms';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = MenuAction & {
  cacheOptions?: boolean;
  className?: string;
  closeMenuOnSelect?: boolean;
  components?: any;
  data?: any;
  disabled?: boolean;
  filterOption?: () => void;
  helpText?: ReactNode;
  hideSelectedOptions?: boolean;
  id?: string;
  isClearable?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  label: string;
  name: string;
  noOptionsMessage?: (values: { inputValue: string }) => ReactNode;
  onChange?: (value: string) => void;
  onClearValue?: () => void;
  onCloseMenuOnScroll?: (event: Event) => void;
  onInputChange?: (value: string) => void;
  onMenuClose?: () => void;
  onMenuScrollToBottom?: () => void;
  options: Array<ItemOption>;
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)`
  column-gap: initial;
  display: block;
`;

/**
 * Export `Select` component.
 */

export function Select(props: Props): JSX.Element {
  const {
    className,
    closeMenuOnSelect = true,
    disabled,
    helpText,
    hideSelectedOptions = false,
    id,
    isClearable = true,
    isMulti = false,
    label,
    name,
    onChange,
    options,
    ...rest
  } = props;

  const { error, meta, onChange: onFieldChange, value, ...fieldProps } = useField(name);
  const { active: isActive } = meta || {};
  const { alreadySubmitted, isSubmitting } = useFormState();
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;
  const handleChange = useCallback((value: string) => {
    if (onChange) {
      onChange(value);
    }

    onFieldChange(value);
  }, [onChange, onFieldChange]);

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
    >
      <StyledFormControlWrapper
        disabled={isDisabled}
        hasError={hasError}
        hasFocus={isActive}
      >
        <Label
          disabled={isDisabled}
          htmlFor={id ?? name}
          id={`${name}_label`}
          isFloating={!isNil(value) || isActive}
        >
          {label}
        </Label>

        <BaseSelect
          aria-describedby={helpText ? `${name}Description` : null}
          closeMenuOnSelect={closeMenuOnSelect}
          disabled={isDisabled}
          hasValues={!isNil(value)}
          hideSelectedOptions={hideSelectedOptions}
          id={id ?? name}
          isClearable={isClearable}
          isMulti={isMulti}
          name={name}
          onChange={handleChange}
          options={options}
          value={value}
          {...rest}
          {...fieldProps}
        />
      </StyledFormControlWrapper>

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </FormGroup>
  );
}
